import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconChevronDown } from "@assets/Icons/IconChevron-Down.svg";
import { splitLogTimestamp } from "./logTimestamp";
import { levelTagVariant, splitMsgId } from "./logMessage";
import { CopyMessageButton } from "./CopyMessageButton";

export interface LogItem {
    /** Local time, `yyyy-MM-dd HH:mm:ss.ffffff` (see formatLogTimestamp). */
    timestamp: string;
    /** The timestamp exactly as the handler returned it, for Copy JSON. */
    rawTimestamp: string;
    level: string;
    message: string;
}

export interface LogsColumnsOptions {
    /** Show the whole message instead of one truncated line. */
    wrapMessages: boolean;
}

/** Points right while the row is closed and down while it is open. */
function ExpandToggle({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
    return (
        <GenericButton
            onClick={onToggle}
            icon={
                <IconChevronDown
                    className={`w-4 h-4 text-semantic-text-secondary transition-transform ${
                        isExpanded ? "" : "-rotate-90"
                    }`}
                />
            }
            isIconOnly
            variant="secondary"
            shape="ghost"
            size="sm"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse log" : "Expand log"}
        />
    );
}

function TimestampCell({ value }: { value: string }) {
    const parts = splitLogTimestamp(value);
    if (!parts) {
        return <span className="text-sm text-semantic-text-secondary">{value}</span>;
    }
    return (
        <div className="flex flex-col leading-tight whitespace-nowrap">
            <span className="text-sm text-semantic-text-primary">
                {parts.time}
                <span className="text-semantic-text-secondary">{parts.fraction}</span>
            </span>
            <span className="text-xs text-semantic-text-secondary">{parts.date}</span>
        </div>
    );
}

/**
 * The MSGID chip sits on its own line above the text. GenericTag is taller than
 * a line of text (its padding applies twice), so inline in a wrapping message
 * it pushed the line apart and the next line ran underneath it.
 */
function MessageCell({ message, wrap }: { message: string; wrap: boolean }) {
    const { msgId, rest } = splitMsgId(message);
    return (
        <div className="flex flex-col items-start gap-1 min-w-0" title={message}>
            {msgId && (
                <GenericTag label={msgId} variant="neutral" size="small" rounded="small" />
            )}
            <span
                className={`max-w-full text-sm text-semantic-text-primary ${
                    wrap ? "break-words" : "truncate"
                }`}
            >
                {rest}
            </span>
        </div>
    );
}

/**
 * chevron | Timestamp | Level | Message | copy — the CTX-1354 proposal, on the
 * platform's table conventions: plain headers, Level and the MSGID chip as
 * `GenericTag` with Reflector V2 Logs' props. Message is shown whole, on one
 * truncated line or wrapped, per the table's Wrap messages switch. The chevron
 * opens the row in place (CTX-1355); the table must be `expandable` with
 * `showExpandColumn={false}`, since the toggle lives in this first column.
 */
export const logsColumns = ({
    wrapMessages,
}: LogsColumnsOptions): GenericTableColumn<LogItem>[] => [
        {
            header: "",
            width: "48px",
            renderCell: (_row, rowIndex, toggleRowExpand, isExpanded) => (
                <ExpandToggle
                    isExpanded={Boolean(isExpanded)}
                    onToggle={() => toggleRowExpand?.(rowIndex)}
                />
            ),
        },
        {
            header: "Timestamp",
            accessor: "timestamp",
            width: "150px",
            renderCell: (row) => <TimestampCell value={row.timestamp} />,
        },
        {
            header: "Level",
            accessor: "level",
            width: "100px",
            renderCell: (row) =>
                row.level ? (
                    <GenericTag
                        label={row.level.toUpperCase()}
                        variant={levelTagVariant(row.level)}
                        size="small"
                        rounded="full"
                        minWidth="60px"
                    />
                ) : null,
        },
        {
            header: "Message",
            wrapMode: wrapMessages ? "break" : "truncate",
            renderCell: (row) => <MessageCell message={row.message} wrap={wrapMessages} />,
        },
        {
            header: "",
            width: "48px",
            renderCell: (row) => <CopyMessageButton message={row.message} />,
        },
    ];
